import { ReactElement } from 'react';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FlexAligner,
  Typography,
} from '@scaleaq/scaleui';
import { background } from '@scaleaq/sui-tokens/colors';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  FeedbackAlert,
  type FeedbackAlertProps,
} from '@/components/feedbackAlert/FeedbackAlert.tsx';
import { Z_INDEX } from '@/constants';

export type ConfirmDeleteDialogProps = DialogProps & {
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;

  itemName: string | undefined;
  feedback?: FeedbackAlertProps;
  /** Clears parent feedback when the alert auto-hides or is dismissed. */
  onFeedbackDismiss?: () => void;
  loading: boolean | undefined;
  disableConfirmDeleteButton?: boolean;
  children?: ReactElement;
};

export const ConfirmDeleteDialog = ({
  open,
  itemName,
  feedback,
  onFeedbackDismiss,
  loading,
  disableConfirmDeleteButton,
  onConfirm,
  onClose,
  children,
  ...rest
}: ConfirmDeleteDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} zIndex={Z_INDEX.overlay.panel} {...rest}>
      <DialogHeader title={`${t('delete')} ${itemName}`} />

      <StyledDialogContent>
        <FlexAligner direction='column' gap={'1rem'}>
          <Typography type='display_small'>{t('confirmDeleteMessage')}</Typography>
          <Typography type='display_small'>
            <b>{itemName}</b>?
          </Typography>
          {children}
        </FlexAligner>
      </StyledDialogContent>
      <FeedbackAlert {...feedback} loading={loading} onDismiss={onFeedbackDismiss} />
      <DialogFooter
        actionButtons={[
          {
            label: t('delete'),
            disabled: disableConfirmDeleteButton,
            ...(!disableConfirmDeleteButton && {
              style: { backgroundColor: background.critical.solid[200] },
            }),

            onClick: onConfirm,
          },
          {
            label: t('cancel'),
            variant: 'outline',
            color: 'neutral',
            onClick: onClose,
          },
        ]}
      />
    </Dialog>
  );
};

// ************************************
// Styled components
// ************************************

const StyledDialogContent = styled(DialogContent)`
  min-height: 7.5rem;
`;
